import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  EMPTY_TOUR,
  WELCOME_TOUR_STEPS,
  clampTourStep,
  completeTour,
  dismissTour,
  restartTour,
  resumeTour,
  type TourProgress,
} from '@/components/onboarding/tourPlan';

interface OnboardingContextType extends TourProgress {
  currentTour: 'welcome' | null;
  hasCompletedOnboarding: boolean;
  hasSkippedOnboarding: boolean;
  completedSteps: string[];
  startOnboarding: (tour?: string) => void;
  stopOnboarding: () => void;
  skipTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void;
  setCurrentTour: (tour: string | null) => void;
  completeStep: (stepId: string) => void;
  skipStep: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

function readRecord(preferences: Record<string, unknown> | null | undefined): TourProgress {
  const onboarding = (preferences?.onboarding ?? {}) as Record<string, unknown>;
  return {
    active: false,
    step: clampTourStep(Number(onboarding.step ?? 0)),
    completed: onboarding.completed === true,
    dismissed: onboarding.dismissed === true || onboarding.skipped === true,
  };
}

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<TourProgress>(EMPTY_TOUR);
  const { user } = useAuth();

  const persist = useCallback(async (next: TourProgress) => {
    if (!user) return;
    try {
      const { data: currentData } = await supabase
        .from('user_settings')
        .select('notification_preferences')
        .eq('user_id', user.id)
        .maybeSingle();
      const currentPreferences = (currentData?.notification_preferences as Record<string, unknown>) || {};
      await supabase.from('user_settings').upsert({
        user_id: user.id,
        notification_preferences: {
          ...currentPreferences,
          onboarding: {
            completed: next.completed,
            dismissed: next.dismissed,
            skipped: next.dismissed,
            step: next.step,
            hasStarted: true,
            lastActive: new Date().toISOString(),
          },
        },
      });
    } catch (error) {
      console.error('Error saving onboarding state:', error);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setState(EMPTY_TOUR);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('user_settings')
        .select('notification_preferences')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled || error || !data) return;
      setState(readRecord(data.notification_preferences as Record<string, unknown>));
    })().catch((error) => console.error('Error loading onboarding state:', error));
    return () => {
      cancelled = true;
    };
  }, [user]);

  const update = (next: TourProgress) => {
    setState(next);
    void persist(next);
  };

  const startOnboarding = () => update(resumeTour({ ...state, completed: false, dismissed: false }));
  const stopOnboarding = () => update(dismissTour(state));
  const skipTour = () => update(dismissTour(state));
  const completeOnboarding = () => update(completeTour(state));
  const resetOnboarding = () => update(restartTour());
  const nextStep = () => setState((prev) => ({ ...prev, step: clampTourStep(prev.step + 1) }));
  const prevStep = () => setState((prev) => ({ ...prev, step: clampTourStep(prev.step - 1) }));
  const goToStep = (step: number) => {
    const next = { ...state, step: clampTourStep(step), active: true };
    setState(next);
    void persist(next);
  };

  const value: OnboardingContextType = {
    ...state,
    currentTour: state.active ? 'welcome' : null,
    hasCompletedOnboarding: state.completed,
    hasSkippedOnboarding: state.dismissed && !state.active,
    completedSteps: state.completed ? WELCOME_TOUR_STEPS.map((step) => step.id) : WELCOME_TOUR_STEPS.slice(0, state.step).map((step) => step.id),
    startOnboarding,
    stopOnboarding,
    skipTour,
    nextStep,
    prevStep,
    goToStep,
    completeOnboarding,
    resetOnboarding,
    setCurrentTour: () => undefined,
    completeStep: () => undefined,
    skipStep: nextStep,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
